import React, {useContext} from 'react'
import {useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {PartialFiltersMatch} from 'components/HotelCard/PartialFiltersMatch/PartialFiltersMatch'
import {HotelCardContext} from 'components/HotelCardContext'
import {LocationDetails} from 'components/HotelCardLocationDetails'
import {
  PropertyHighlights,
  usePropertyHighlights
} from 'components/PropertyHighlights'
import {HotelMoreDetails} from 'components/SeoSRP/HotelMoreDetails/HotelMoreDetails'
import {VacationRentalDetails} from 'components/VacationRentalDetails'
import {useIsSeoSRP} from 'hooks/useIsSeoSRP'
import {getHotel, State} from 'modules/sapiSearch/selectors'
import {getListOfValues} from 'modules/search/selectors'
import {getRankedAvailableFacilities} from 'utils/facilities'
import {SelectedElement} from 'utils/selectedElement'

import {Box} from '@vio/atlas/helpers/Box'
import {StarRating} from '@vio/atlas/StarRating'
import {cssTheme} from '@vio/atlas/themes'
import {ReviewRating} from '@vio/shared/src/search/ReviewRating'

import {HotelBadges} from '../../HotelCard/HotelBadges/HotelBadges'
import {PropertySummaryLoading} from './PropertySummaryLoading'

const HotelName = styled.h2`
  ${cssTheme.typography.titleL}
  word-wrap: break-word;
  margin: ${cssTheme.layout.spacing.s200} 0;
`

export const PropertySummary: React.FC = () => {
  const {hotelId, hotelDetailsIsOpenOnLanding, isAnchor} =
    useContext(HotelCardContext)
  const hotel = useSelector((state: State) => getHotel(state, hotelId))
  const listOfValues = useSelector(getListOfValues)
  const rankedFacilities = getRankedAvailableFacilities(
    hotel?.facilities,
    listOfValues
  )

  const isSeoSrp = useIsSeoSRP()
  // Added as part of dcbc5616-property-highlights-v1
  const highlights = usePropertyHighlights(hotelId)

  // Showing skeleton
  if (!hotel || !hotelId) return <PropertySummaryLoading />

  const showRankedFacilities = hotelDetailsIsOpenOnLanding && isAnchor
  return (
    <Box
      display="flex"
      flexDirection="column"
      height="100%"
      alignContent="space-between"
    >
      {/* Highlights replace the badges. Added as part of dcbc5616-property-highlights-v1 */}
      {!highlights.show && <HotelBadges hotelId={hotelId} />}
      <div data-id={SelectedElement.StarRating}>
        <StarRating size="xs" rating={hotel.starRating} />
      </div>
      <HotelName data-id={SelectedElement.HotelName}>
        {hotel.hotelName}
      </HotelName>
      <LocationDetails placeName={hotel.placeDisplayName} />
      {/* Added as part of dcbc5616-property-highlights-v1 */}
      <PropertyHighlights items={highlights.location} />

      <Box display="inline-block" paddingTop="s300">
        <ReviewRating
          rating={hotel?.guestRating.overall}
          totalRatingCount={hotel.reviewCount}
          hotelId={hotel.objectID}
        />
        {isSeoSrp && <HotelMoreDetails hotel={hotel} />}
      </Box>
      {/* Added as part of dcbc5616-property-highlights-v1 */}
      {highlights.themes.length > 0 && (
        <Box paddingTop="s300">
          <PropertyHighlights items={highlights.themes} />
        </Box>
      )}

      {/* The s000 padding is part of dcbc5616-property-highlights-v1 */}
      <Box
        paddingTop={highlights.show ? 's000' : 's300'}
        paddingBottom={highlights.show ? 's000' : 's300'}
      >
        <PartialFiltersMatch
          hasBasicPropertyDetails={hotelDetailsIsOpenOnLanding}
        />
      </Box>

      {showRankedFacilities && (
        <Box
          paddingTop="s400"
          paddingBottom="s400"
          paddingLeft="s000"
          paddingRight="s000"
        >
          <VacationRentalDetails
            showVrDetails={hotel?.isVr}
            vrDetails={hotel?.vrDetails || {}}
            rankedFacilities={rankedFacilities}
          />
        </Box>
      )}
    </Box>
  )
}
